import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const ACTIVITY_LOG_ENDPOINT = "/api/support-log-visit.php";

const SUPPORT_WIDGET_ENABLED = import.meta.env.VITE_SUPPORT_CHAT_ENABLED !== "false";
const SESSION_STORAGE_KEY = "gainwise_support_activity_session_v1";

const routeLabels = {
  "/": "Home",
  "/about": "About",
  "/contact": "Contact",
  "/login": "Login",
  "/signup": "Sign Up",
  "/dashboard": "Dashboard",
  "/trade": "Markets",
  "/trade-live": "Trade Live",
  "/wallet": "Wallet",
  "/profile": "Profile",
  "/settings": "Settings",
  "/payment": "Payment",
  "/payment-callback": "Payment Callback",
};

const getRouteLabel = (pathname) =>
  routeLabels[pathname] || pathname.replace(/^\//, "").replace(/-/g, " ") || "Home";

const getOrCreateSessionId = () => {
  if (typeof window === "undefined") {
    return "server";
  }

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const created = `activity-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
  return created;
};

const detectDeviceType = () => {
  if (typeof window === "undefined") {
    return "desktop";
  }

  const width = window.innerWidth;
  if (width < 768) {
    return "mobile";
  }
  if (width < 1100) {
    return "tablet";
  }
  return "desktop";
};

const SupportActivityTracker = () => {
  const { user, token, isAuthenticated } = useAuth();
  const location = useLocation();
  const sessionIdRef = useRef(getOrCreateSessionId());
  const sequenceNumberRef = useRef(0);
  const lastPathRef = useRef(location.pathname);
  const lastVisitedAtRef = useRef(Date.now());
  const lastVisitSignatureRef = useRef("");

  const isMember = isAuthenticated && !!user;
  const userEmail = user?.email || "";
  const userId = user?.id || "";

  const sendVisit = (payload) => {
    const query = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      query.append(key, String(value));
    });
    query.append("_ts", String(Date.now()));

    const requestUrl = `${ACTIVITY_LOG_ENDPOINT}?${query.toString()}`;

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const beaconPayload = new Blob([], { type: "text/plain" });
      navigator.sendBeacon(requestUrl, beaconPayload);
      return Promise.resolve();
    }

    if (typeof Image !== "undefined") {
      return new Promise((resolve) => {
        const trackerImage = new Image();
        trackerImage.onload = () => resolve();
        trackerImage.onerror = () => resolve();
        trackerImage.src = requestUrl;
      });
    }

    return fetch(requestUrl, {
      method: "GET",
      keepalive: true,
      credentials: "same-origin",
    }).then(() => undefined);
  };

  useEffect(() => {
    if (!SUPPORT_WIDGET_ENABLED || !isMember || (!userEmail && !userId)) {
      lastPathRef.current = location.pathname;
      lastVisitedAtRef.current = Date.now();
      return undefined;
    }

    const pathname = location.pathname;
    const referrerPath = lastPathRef.current;
    const durationMs = Math.max(0, Date.now() - lastVisitedAtRef.current);
    const sourceType = referrerPath === pathname ? "direct" : "internal";
    const visitSignature = [
      pathname,
      referrerPath || "",
      sourceType,
      userId,
      userEmail,
    ].join("|");

    if (lastVisitSignatureRef.current !== visitSignature) {
      sendVisit({
          userId,
          userEmail,
          guestEmail: userEmail,
          path: pathname,
          pageLabel: getRouteLabel(pathname),
          referrerPath,
          sourceType,
          deviceType: detectDeviceType(),
          sessionId: sessionIdRef.current,
          sequenceNumber: sequenceNumberRef.current + 1,
          durationMs,
        }).catch(() => {
        // Activity tracking should never interrupt the app.
      });

      lastVisitSignatureRef.current = visitSignature;
      sequenceNumberRef.current += 1;
    }

    lastPathRef.current = pathname;
    lastVisitedAtRef.current = Date.now();
    return undefined;
  }, [isMember, location.pathname, userEmail, userId]);

  return null;
};

export default SupportActivityTracker;
